import { useEffect, useMemo, useRef } from 'react';
import fallbackImage from '../../assets/generated/space-reveal-galactic-swirl.jpg';

// Faithful port of the supplied ink-reveal component (21st.dev @aayush-duhan),
// inverted for this site: instead of carving holes in an opaque overlay, the
// organic wobble-stamps form an alpha mask that space imagery is composited
// INTO ('source-in'), so the cursor trail reveals a hidden second imagery
// layer over the live page. Stamp system (expand/bloom/heal, stroke
// interpolation, prune) matches the source component. The image is tone-baked
// dark at load (cool multiply grade) so the reveal reads as a quiet deeper
// layer of space, never a bright flashlight.
const REVEAL_IMAGE = '/assets/images/galaxy-phantom.jpg';
const GRADE_MULTIPLY = '#667190'; // cool slate — ≈ ×0.40/0.44/0.56 per channel
const GRADE_LIFT = 'rgba(20, 26, 44, 0.22)'; // faint indigo lift so shadows stay spatial

function toneImage(img) {
  const c = document.createElement('canvas');
  c.width = img.naturalWidth;
  c.height = img.naturalHeight;
  const x = c.getContext('2d');
  x.drawImage(img, 0, 0);
  x.globalCompositeOperation = 'multiply';
  x.fillStyle = GRADE_MULTIPLY;
  x.fillRect(0, 0, c.width, c.height);
  x.globalCompositeOperation = 'lighter';
  x.fillStyle = GRADE_LIFT;
  x.fillRect(0, 0, c.width, c.height);
  x.globalCompositeOperation = 'source-over';
  return c;
}

const BRUSH_SIZE = 140;
const LIFETIME = 900;
const R_START = 10;
const R_VARY = 0.45;
const STAMP_STEP = 10;
const MAX_STAMPS = 200;
const SEGMENTS = 36;
const WOBBLE = [0.14, 0.08, 0.05];
const GRADIENT_INNER = 0.2;
const GRADIENT_STOPS = [0.95, 0.88, 0];

export function InkRevealCursor() {
  const canvasRef = useRef(null);
  const stampsRef = useRef([]);
  const runningRef = useRef(false);
  const lastPosRef = useRef(null);
  const dimsRef = useRef({ w: 0, h: 0 });
  const imageRef = useRef(null);

  const enabled = useMemo(
    () =>
      typeof window !== 'undefined' &&
      window.matchMedia('(pointer: fine)').matches &&
      !window.matchMedia('(prefers-reduced-motion: reduce)').matches,
    [],
  );

  useEffect(() => {
    if (!enabled) return undefined;
    const img = new Image();
    img.onload = () => {
      imageRef.current = toneImage(img);
    };
    img.onerror = () => {
      const fb = new Image();
      fb.onload = () => {
        imageRef.current = toneImage(fb);
      };
      fb.src = fallbackImage;
    };
    img.src = REVEAL_IMAGE;
    return () => {
      imageRef.current = null;
    };
  }, [enabled]);

  useEffect(() => {
    if (!enabled) return undefined;
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext('2d');

    function resize() {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const w = window.innerWidth;
      const h = window.innerHeight;
      dimsRef.current = { w, h };
      canvas.width = Math.round(w * dpr);
      canvas.height = Math.round(h * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    }
    resize();
    window.addEventListener('resize', resize);

    function carveInk(x, y, r, seed, alpha) {
      const g = ctx.createRadialGradient(x, y, r * GRADIENT_INNER, x, y, r);
      g.addColorStop(0, `rgba(0,0,0,${GRADIENT_STOPS[0] * alpha})`);
      g.addColorStop(0.5, `rgba(0,0,0,${GRADIENT_STOPS[1] * alpha})`);
      g.addColorStop(1, `rgba(0,0,0,${GRADIENT_STOPS[2] * alpha})`);
      ctx.fillStyle = g;
      ctx.beginPath();
      for (let i = 0; i <= SEGMENTS; i++) {
        const a = (i / SEGMENTS) * Math.PI * 2;
        const wob =
          0.78 +
          WOBBLE[0] * Math.sin(a * 3 + seed) +
          WOBBLE[1] * Math.sin(a * 5 + seed * 2.1) +
          WOBBLE[2] * Math.sin(a * 7 + seed * 0.7);
        const px = x + Math.cos(a) * r * wob;
        const py = y + Math.sin(a) * r * wob;
        if (i === 0) ctx.moveTo(px, py);
        else ctx.lineTo(px, py);
      }
      ctx.closePath();
      ctx.fill();
    }

    function drawCover(img, w, h) {
      const iw = img.naturalWidth ?? img.width;
      const ih = img.naturalHeight ?? img.height;
      const scale = Math.max(w / iw, h / ih);
      ctx.drawImage(img, (w - iw * scale) / 2, (h - ih * scale) / 2, iw * scale, ih * scale);
    }

    function loop() {
      const { w, h } = dimsRef.current;
      const now = performance.now();
      const stamps = stampsRef.current;

      ctx.clearRect(0, 0, w, h);
      ctx.globalCompositeOperation = 'source-over';
      for (let i = stamps.length - 1; i >= 0; i--) {
        const t = (now - stamps[i].born) / LIFETIME;
        if (t >= 1) {
          stamps.splice(i, 1);
          continue;
        }
        const ease = 1 - Math.pow(1 - t, 3);
        const r = R_START + (stamps[i].rmax - R_START) * ease;
        const alpha = 1 - t * t;
        carveInk(stamps[i].x, stamps[i].y, r, stamps[i].seed, alpha);
      }

      if (stamps.length && imageRef.current) {
        ctx.globalCompositeOperation = 'source-in';
        drawCover(imageRef.current, w, h);
        ctx.globalCompositeOperation = 'source-over';
      }

      if (stamps.length) {
        requestAnimationFrame(loop);
      } else {
        ctx.clearRect(0, 0, w, h);
        runningRef.current = false;
      }
    }

    function startLoop() {
      if (!runningRef.current) {
        runningRef.current = true;
        requestAnimationFrame(loop);
      }
    }

    function addStamp(x, y) {
      const stamps = stampsRef.current;
      if (stamps.length >= MAX_STAMPS) stamps.shift();
      stamps.push({
        x,
        y,
        born: performance.now(),
        seed: Math.random() * Math.PI * 2,
        rmax: BRUSH_SIZE * (1 - R_VARY + Math.random() * R_VARY),
      });
    }

    function handlePointerMove(event) {
      const x = event.clientX;
      const y = event.clientY;
      const last = lastPosRef.current;
      if (!last) {
        addStamp(x, y);
      } else {
        const dx = x - last.x;
        const dy = y - last.y;
        const dist = Math.hypot(dx, dy);
        const steps = Math.max(1, Math.ceil(dist / STAMP_STEP));
        for (let i = 1; i <= steps; i++) {
          addStamp(last.x + (dx * i) / steps, last.y + (dy * i) / steps);
        }
      }
      lastPosRef.current = { x, y };
      startLoop();
    }

    function handlePointerLeave() {
      lastPosRef.current = null;
    }

    window.addEventListener('pointermove', handlePointerMove, { passive: true });
    document.documentElement.addEventListener('pointerleave', handlePointerLeave);
    return () => {
      window.removeEventListener('resize', resize);
      window.removeEventListener('pointermove', handlePointerMove);
      document.documentElement.removeEventListener('pointerleave', handlePointerLeave);
    };
  }, [enabled]);

  if (!enabled) return null;

  return <canvas ref={canvasRef} className="ink-canvas" aria-hidden="true" />;
}
